import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  forkJoin,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';
import {
  Player,
  PlayerSeasonStatsDTO,
  Season,
  StatisticsService
} from '@solverml/api';

/**
 * "Cerebro" (State Service) para la página de detalle del jugador.
 * Gestiona la carga del perfil, las temporadas disponibles y las
 * estadísticas de la temporada seleccionada.
 */
@Injectable({
  providedIn: 'root'
})
export class PlayerDetailStateService implements OnDestroy {

  // --- 1. ESTADO DE CARGA ---
  // Carga principal de la página (perfil + lista de temporadas)
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  // Carga de las estadísticas (para el dashboard, se activa en la carga inicial y al cambiar de temporada)
  private isLoadingStatsSubject = new BehaviorSubject<boolean>(false);
  public isLoadingStats$ = this.isLoadingStatsSubject.asObservable();

  // --- 2. ESTADO DE DATOS ---
  private playerProfileSubject = new BehaviorSubject<Player | null>(null);
  public playerProfile$ = this.playerProfileSubject.asObservable();

  private playerSeasonsSubject = new BehaviorSubject<Season[] | null>(null);
  public playerSeasons$ = this.playerSeasonsSubject.asObservable();

  private selectedSeasonStatsSubject = new BehaviorSubject<PlayerSeasonStatsDTO | null>(null);
  public selectedSeasonStats$ = this.selectedSeasonStatsSubject.asObservable();

  // --- 3. INTERNO ---
  private destroy$ = new Subject<void>();
  private currentPlayerId: string | null = null;

  constructor(private statsApi: StatisticsService) {}

  /**
   * Limpia todos los observables y resetea el estado.
   * Debe ser llamado en el ngOnDestroy del PageComponent.
   */
  public resetState(): void {
    this.isLoadingSubject.next(false);
    this.isLoadingStatsSubject.next(false);
    this.playerProfileSubject.next(null);
    this.playerSeasonsSubject.next(null);
    this.selectedSeasonStatsSubject.next(null);
    this.currentPlayerId = null;
  }

  ngOnDestroy(): void {
    // Limpia las suscripciones internas
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga los datos iniciales del jugador (Perfil, Temporadas y Stats de la primera temporada).
   * @param playerId El ID del jugador (obtenido de la ruta).
   */
  public loadData(playerId: string): void {
    // Si ya estamos cargando o el ID es el mismo, no hacemos nada
    if (this.isLoadingSubject.value && this.currentPlayerId === playerId) {
      return;
    }

    this.isLoadingSubject.next(true);
    this.currentPlayerId = playerId;

    // Reseteamos el estado anterior
    this.playerProfileSubject.next(null);
    this.playerSeasonsSubject.next(null);
    this.selectedSeasonStatsSubject.next(null);

    // 1. Cargamos Perfil y Lista de Temporadas en paralelo
    forkJoin({
      profile: this.statsApi.getPlayerProfileEndpoint(playerId),
      seasons: this.statsApi.getPlayerSeasonsEndpoint(playerId)
    }).pipe(
      switchMap(({ profile, seasons }) => {
        // 2. Actualizamos los sujetos de perfil y temporadas
        this.playerProfileSubject.next(profile);
        this.playerSeasonsSubject.next(seasons);

        // 3. Si hay temporadas, cargamos las estadísticas de la primera (la más reciente)
        if (seasons && seasons.length > 0) {
          const defaultSeasonId = seasons[0].id;
          // Activamos el loader del dashboard
          this.isLoadingStatsSubject.next(true);

          return this.statsApi.getPlayerStatsBySeasonEndpoint(playerId, defaultSeasonId).pipe(
            tap(stats => {
              this.selectedSeasonStatsSubject.next(stats);
            }),
            catchError(err => {
              console.error(`Error loading default stats for player ${playerId}`, err);
              this.selectedSeasonStatsSubject.next(null);
              return of(null);
            }),
            finalize(() => {
              this.isLoadingStatsSubject.next(false);
            })
          );
        } else {
          // No hay temporadas, no hay stats que cargar
          this.selectedSeasonStatsSubject.next(null);
          return of(null);
        }
      }),
      catchError(err => {
        console.error(`Error loading player profile/seasons for player ${playerId}`, err);
        this.resetState(); // Reseteo completo en caso de error fatal
        return of(null);
      }),
      finalize(() => {
        this.isLoadingSubject.next(false); // Fin de la carga principal
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  /**
   * Carga las estadísticas para una temporada específica (ej. al cambiar el <mat-select>).
   * @param seasonId ID de la temporada seleccionada.
   */
  public changeSeason(seasonId: number): void {
    if (!this.currentPlayerId) {
      console.warn("Cannot change season, currentPlayerId is null.");
      return;
    }

    this.isLoadingStatsSubject.next(true); // Activar loader del dashboard
    this.selectedSeasonStatsSubject.next(null); // Limpiar stats antiguas

    this.statsApi.getPlayerStatsBySeasonEndpoint(this.currentPlayerId, seasonId).pipe(
      tap(stats => {
        this.selectedSeasonStatsSubject.next(stats);
      }),
      catchError(err => {
        console.error(`Error loading stats for season ${seasonId}`, err);
        this.selectedSeasonStatsSubject.next(null); // Dejar en blanco si falla
        return of(null);
      }),
      finalize(() => {
        this.isLoadingStatsSubject.next(false); // Desactivar loader
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }
}
